import { useEffect, useState, useRef } from 'react'
import { FiArrowDown, FiMessageCircle } from 'react-icons/fi'
import HeroDevice from './HeroDevice'
import Button from './ui/Button'
import Reveal from './ui/Reveal'
import CountUp from './ui/CountUp'

const Hero = () => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 })
  const [statsInView, setStatsInView] = useState(false)
  const statsRef = useRef(null)

  useEffect(() => {
    const el = statsRef.current
    if (!el) return
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setStatsInView(true)
      },
      { threshold: 0.3 }
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth - 0.5) * 20,
        y: (e.clientY / window.innerHeight - 0.5) * 20,
      })
    }

    window.addEventListener('mousemove', handleMouseMove)
    return () => window.removeEventListener('mousemove', handleMouseMove)
  }, [])

  const scrollToContact = () => {
    const element = document.getElementById('contact')
    if (element) {
      const headerOffset = 80
      const elementPosition = element.getBoundingClientRect().top
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' })
    }
  }

  const scrollToPricing = () => {
    const element = document.getElementById('pricing')
    if (element) {
      const headerOffset = 80
      const elementPosition = element.getBoundingClientRect().top
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' })
    }
  }

  return (
    <section
      id="home"
      className="relative min-h-[calc(100svh)] sm:min-h-[calc(100svh)] lg:min-h-[calc(100svh)] flex items-center justify-center pt-14 sm:pt-16 md:pt-18 lg:pt-20 pb-4 sm:pb-6 md:pb-8 overflow-hidden bg-gradient-to-br from-white via-gray-50/50 to-white dark:from-gray-900 dark:via-gray-900 dark:to-emerald-950/30"
    >
      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Gradient Orbs */}
        <div 
          className="absolute top-1/4 -left-1/4 w-96 h-96 bg-accent/10 rounded-full blur-3xl"
          style={{
            transform: `translate(${mousePosition.x * 0.5}px, ${mousePosition.y * 0.5}px)`,
          }}
        />
        <div 
          className="absolute bottom-1/4 -right-1/4 w-[500px] h-[500px] bg-accent/5 rounded-full blur-3xl"
          style={{
            transform: `translate(${mousePosition.x * -0.3}px, ${mousePosition.y * -0.3}px)`,
          }}
        />
        
        {/* Grid Pattern */}
        <div 
          className="absolute inset-0 opacity-[0.02] dark:opacity-[0.05]"
          style={{
            backgroundImage: `
              linear-gradient(rgba(34, 198, 94, 0.1) 1px, transparent 1px),
              linear-gradient(90deg, rgba(34, 198, 94, 0.1) 1px, transparent 1px)
            `,
            backgroundSize: '50px 50px',
          }}
        />
      </div>

      <div className="container-custom relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-12 items-center w-full min-w-0">
          {/* Content */}
          <Reveal direction="up" delay={0}>
            <div className="text-center lg:text-left space-y-4 sm:space-y-5">
              {/* Badge */}
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent/15 dark:bg-accent/25 border-2 border-accent/40 text-accent text-xs font-extrabold tracking-widest uppercase mb-3 badge-shine">
                <span className="w-2 h-2 bg-accent rounded-full animate-pulse shadow-lg shadow-accent/50" />
                Tecnologia de Monitoramento Inteligente
              </div>

              {/* Title */}
              <h1 className="text-2xl xs:text-3xl sm:text-4xl md:text-5xl lg:text-[2.75rem] xl:text-[3.25rem] font-extrabold leading-[1.08] tracking-tight break-words">
                <span className="block mb-1 drop-shadow-sm title-gradient-animated">Monitoramento</span>
                <span className="block text-gray-900 dark:text-white">Inteligente de Volume</span>
              </h1>

              {/* Description */}
              <p className="text-base sm:text-lg md:text-xl text-gray-600 dark:text-gray-300 leading-relaxed max-w-xl mx-auto lg:mx-0 font-medium">
                Tecnologia de ponta para controle preciso de reservatórios industriais.
                <br className="hidden sm:block" />
                Monitore, analise e otimize seus processos em tempo real.
              </p>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 pt-2 justify-center lg:justify-start">
                <Button
                  onClick={scrollToContact}
                  variant="primary"
                  size="lg"
                  className="group"
                >
                  <FiMessageCircle className="w-5 h-5 mr-2 group-hover:translate-x-1 transition-transform" />
                  Fale Conosco
                </Button>
                <Button
                  onClick={scrollToPricing}
                  variant="secondary"
                  size="lg"
                  className="group"
                >
                  Ver Planos
                  <FiArrowDown className="w-5 h-5 ml-2 group-hover:translate-y-1 transition-transform" />
                </Button>
              </div>

              {/* Stats */}
              <div ref={statsRef} className="grid grid-cols-3 gap-3 sm:gap-6 pt-5 sm:pt-8 border-t-2 border-gray-200/90 dark:border-gray-700/90">
                <div className="text-center lg:text-left">
                  <div className="text-xl sm:text-2xl font-extrabold text-gray-900 dark:text-white tabular-nums">
                    <CountUp end={97} trigger={statsInView} />%
                  </div>
                  <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 mt-1 uppercase tracking-wider">Precisão</div>
                </div>
                <div className="text-center lg:text-left">
                  <div className="text-xl sm:text-2xl font-extrabold text-gray-900 dark:text-white tabular-nums">
                    <CountUp end={24} trigger={statsInView} />/7
                  </div>
                  <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 mt-1 uppercase tracking-wider">Monitoramento</div>
                </div>
                <div className="text-center lg:text-left">
                  <div className="text-xl sm:text-2xl font-extrabold text-gray-900 dark:text-white">Alertas</div>
                  <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 mt-1 uppercase tracking-wider">Automáticos</div>
                </div>
              </div>
            </div>
          </Reveal>

          {/* Illustration - dispositivo com tela do sistema */}
          <Reveal direction="up" delay={200} className="hidden lg:block">
            <div className="relative flex justify-center items-center">
              {/* Glow atrás do dispositivo */}
              <div className="absolute inset-0 bg-accent/8 dark:bg-accent/15 rounded-full blur-3xl transform scale-150 animate-pulse-slow" />
              <div className="relative z-10 transition-transform duration-500 hover:scale-[1.02]">
                <HeroDevice />
              </div>
            </div>
          </Reveal>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 hidden sm:block animate-scroll-breathing">
        <button
          onClick={scrollToPricing}
          className="flex flex-col items-center gap-2 text-gray-400 hover:text-accent transition-colors group"
          aria-label="Rolar para baixo"
        >
          <span className="text-xs font-medium uppercase tracking-wider">Rolar</span>
          <div className="w-6 h-10 border-2 border-current rounded-full flex justify-center pt-2">
            <div className="w-1.5 h-3 bg-current rounded-full animate-bounce" />
          </div>
        </button>
      </div>
    </section>
  )
}

export default Hero
